/**
 * @file useProfessionalLocations.js
 *
 * useProfessionalLocations
 *
 * Manages the list of office locations for the logged-in professional.
 * Used in MyProfile.jsx to let professionals add/remove their locations.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { professionalLocationService } from "../services/professionalLocation";

/**
 * Hook for managing the logged-in professional's office locations (CRUD).
 *
 * @returns {{locations: Array, isLoading: boolean, addLocation: Function, updateLocation: Function, removeLocation: Function, refresh: Function}}
 */
export function useProfessionalLocations() {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await professionalLocationService.getAll();
      setLocations(res.data || []);
    } catch {
      // Error handled by interceptor
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const addLocation = async (data) => {
    const res = await professionalLocationService.create(data);
    setLocations((prev) => [...prev, res.data]);
    toast.success("Ubicación agregada");
    return res.data;
  };

  const updateLocation = async (id, data) => {
    const res = await professionalLocationService.update(id, data);
    setLocations((prev) => prev.map((l) => l.id === id ? res.data : l));
    toast.success("Ubicación actualizada");
    return res.data;
  };

  const removeLocation = async (id) => {
    await professionalLocationService.remove(id);
    setLocations((prev) => prev.filter((l) => l.id !== id));
    toast.success("Ubicación eliminada");
  };

  return { locations, isLoading, addLocation, updateLocation, removeLocation, refresh: fetchLocations };
}
